import React, { useState, useRef, useEffect } from 'react';

// Format relative time (e.g., "2 hours ago", "Yesterday")
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFullDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateSeparator(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();
  
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function shouldShowDateSeparator(currentNote, previousNote) {
  if (!previousNote) return true;
  const currentDate = new Date(currentNote.createdAt).toDateString();
  const previousDate = new Date(previousNote.createdAt).toDateString();
  return currentDate !== previousDate;
}

// Parse markdown-style links and auto-detect URLs in content
function parseContent(content) {
  // First, parse markdown-style links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  // Then, detect raw URLs (http, https, or www.)
  const urlRegex = /(https?:\/\/[^\s<>[\]()]+|www\.[^\s<>[\]()]+)/gi;
  
  const parts = [];
  let lastIndex = 0;
  
  // Combined regex to match both markdown links and raw URLs
  // We process the content character by character to handle both patterns
  const combinedRegex = /(\[([^\]]+)\]\(([^)]+)\))|(https?:\/\/[^\s<>[\]()]+|www\.[^\s<>[\]()]+)/gi;
  let match;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
    }
    
    if (match[1]) {
      // Markdown-style link: [text](url)
      parts.push({ type: 'link', text: match[2], url: match[3] });
    } else if (match[4]) {
      // Raw URL
      let url = match[4];
      // Add https:// if URL starts with www.
      const href = url.startsWith('www.') ? `https://${url}` : url;
      parts.push({ type: 'link', text: url, url: href });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.substring(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

// Render content with parsed links and markdown-style bold
function RenderContent({ content, className = '' }) {
  const parts = parseContent(content);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === 'link') {
          return (
            <a
              key={index}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium hover:opacity-80 transition-opacity"
            >
              {part.text}
            </a>
          );
        }
        const boldParts = part.content.split(/\*\*([^*]+)\*\*/g);
        return boldParts.map((text, i) => {
          if (i % 2 === 1) {
            return <strong key={`${index}-${i}`} className="font-semibold">{text}</strong>;
          }
          return <span key={`${index}-${i}`}>{text}</span>;
        });
      })}
    </span>
  );
}

function DateSeparator({ date }) {
  return (
    <div className="flex items-center justify-center my-6">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      <span className="px-4 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full mx-4 shadow-sm">
        {formatDateSeparator(date)}
      </span>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    </div>
  );
}

function SystemEventItem({ note }) {
  return (
    <div className="flex justify-center my-4">
      <div className="max-w-lg w-full">
        <div className="relative">
          {/* Decorative line */}
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-dashed border-zapt-purple/20" />
          </div>
          {/* Content */}
          <div className="relative flex justify-center">
            <div className="bg-gradient-to-br from-zapt-purple/5 via-white to-zapt-purple/5 border border-zapt-purple/20 rounded-2xl px-5 py-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-zapt-purple to-zapt-purple/80 rounded-xl flex items-center justify-center shadow-md">
                  <span className="text-base">📦</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    <RenderContent content={note.content} />
                  </p>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formatRelativeTime(note.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRequestItem({ note, isCurrentUser }) {
  return (
    <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''} mb-1`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
          isCurrentUser 
            ? 'bg-gradient-to-br from-amber-500 to-amber-600' 
            : 'bg-gradient-to-br from-amber-400 to-amber-500'
        }`}>
          <span className="text-lg">💡</span>
        </div>
      </div>
      
      {/* Message bubble */}
      <div className={`flex-1 max-w-[85%] sm:max-w-[75%] ${isCurrentUser ? 'flex flex-col items-end' : ''}`}>
        {/* Sender name */}
        <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
          <span className="text-xs font-medium text-gray-600">
            {isCurrentUser ? 'You' : (note.authorEmail?.split('@')[0] || 'User')}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <span>✨</span> Feature Request
          </span>
        </div>
        
        {/* Bubble */}
        <div className={`relative group ${isCurrentUser ? 'text-right' : ''}`}>
          <div className={`inline-block text-left rounded-2xl px-4 py-3 shadow-sm ${
            isCurrentUser 
              ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-br-md' 
              : 'bg-amber-50 border border-amber-200 text-gray-800 rounded-bl-md'
          }`}>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              <RenderContent content={note.content} />
            </p>
          </div>
          
          {/* Timestamp */}
          <div className={`mt-1 flex items-center gap-2 ${isCurrentUser ? 'justify-end' : ''}`}>
            <span className={`text-xs ${isCurrentUser ? 'text-gray-400' : 'text-gray-400'}`} title={formatFullDateTime(note.createdAt)}>
              {formatRelativeTime(note.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageItem({ note, isCurrentUser, isAdmin }) {
  const isAdminMessage = !isCurrentUser && isAdmin;
  
  return (
    <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''} mb-1`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shadow-md transition-transform hover:scale-105 ${
          isCurrentUser 
            ? 'bg-gradient-to-br from-zapt-purple to-zapt-purple/80 text-white' 
            : isAdminMessage 
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white'
              : 'bg-gradient-to-br from-gray-400 to-gray-500 text-white'
        }`}>
          {isAdminMessage ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ) : (
            note.authorEmail?.charAt(0).toUpperCase() || '?'
          )}
        </div>
      </div>
      
      {/* Message bubble */}
      <div className={`flex-1 max-w-[85%] sm:max-w-[75%] ${isCurrentUser ? 'flex flex-col items-end' : ''}`}>
        {/* Sender name */}
        <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
          <span className={`text-xs font-medium ${isAdminMessage ? 'text-emerald-600' : 'text-gray-600'}`}>
            {isCurrentUser ? 'You' : isAdminMessage ? 'ZAPT Team' : (note.authorEmail?.split('@')[0] || 'User')}
          </span>
          {isAdminMessage && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </span>
          )}
        </div>
        
        {/* Bubble */}
        <div className={`relative group ${isCurrentUser ? 'text-right' : ''}`}>
          <div className={`inline-block text-left rounded-2xl px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${
            isCurrentUser 
              ? 'bg-gradient-to-br from-zapt-purple to-zapt-purple/90 text-white rounded-br-md' 
              : isAdminMessage
                ? 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 text-gray-800 rounded-bl-md'
                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
          }`}>
            <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isCurrentUser ? 'text-white' : ''}`}>
              <RenderContent content={note.content} />
            </p>
          </div>
          
          {/* Timestamp */}
          <div className={`mt-1 flex items-center gap-1 ${isCurrentUser ? 'justify-end' : ''}`}>
            <span className="text-xs text-gray-400" title={formatFullDateTime(note.createdAt)}>
              {formatRelativeTime(note.createdAt)}
            </span>
            {isCurrentUser && (
              <svg className="w-3.5 h-3.5 text-zapt-purple/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin email check (simplified - in production, this should come from backend)
const ADMIN_EMAIL_DOMAINS = ['zapt.ai'];
function isAdminEmail(email) {
  if (!email) return false;
  return ADMIN_EMAIL_DOMAINS.some(domain => email.endsWith(`@${domain}`));
}

export default function ConversationTimeline({ 
  notes = [], 
  currentUserEmail,
  onSendMessage,
  onRequestFeature,
  addingNote = false,
  dark = false,
  isAdmin = false,
}) {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [notes]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || addingNote) return;
    await onSendMessage(newMessage.trim());
    setNewMessage('');
    // Focus back on textarea after sending
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setNewMessage(e.target.value);
    // Auto-resize
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
  };

  // Theme-aware styling
  const containerClasses = dark 
    ? "bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl overflow-hidden"
    : "bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden";
  const headerClasses = dark
    ? "px-4 sm:px-6 py-4 border-b border-white/10 bg-white/5"
    : "px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white";
  const titleClasses = dark ? "text-white" : "text-gray-900";
  const subtitleClasses = dark ? "text-gray-400" : "text-gray-500";
  const messagesAreaClasses = dark
    ? "p-4 sm:p-6 space-y-4 max-h-[600px] overflow-y-auto scroll-smooth"
    : "p-4 sm:p-6 space-y-4 max-h-[600px] overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white scroll-smooth";
  const inputAreaClasses = dark
    ? "px-4 sm:px-6 py-4 border-t border-white/10 bg-white/5"
    : "px-4 sm:px-6 py-4 border-t border-gray-100 bg-white";
  const inputWrapperClasses = dark
    ? "relative bg-white/10 rounded-2xl border border-white/20 focus-within:border-zapt-yellow/50 focus-within:ring-2 focus-within:ring-zapt-yellow/20 transition-all overflow-hidden"
    : "relative bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-zapt-purple focus-within:ring-2 focus-within:ring-zapt-purple/20 transition-all overflow-hidden";
  const textareaClasses = dark
    ? "w-full px-4 py-3 pr-12 bg-transparent border-0 focus:ring-0 resize-none text-sm text-white placeholder-gray-400"
    : "w-full px-4 py-3 pr-12 bg-transparent border-0 focus:ring-0 resize-none text-sm text-gray-800 placeholder-gray-400";
  const helperTextClasses = dark ? "text-gray-500" : "text-gray-400";
  const kbdClasses = dark ? "px-1.5 py-0.5 bg-white/10 rounded text-gray-400 text-xs font-mono" : "px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 text-xs font-mono";

  return (
    <div className={containerClasses}>
      {/* Header */}
      <div className={headerClasses}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-zapt-purple to-zapt-purple/80 rounded-xl flex items-center justify-center shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className={`text-lg font-bold ${titleClasses}`}>Updates & Messages</h2>
              <p className={`text-xs ${subtitleClasses}`}>
                {notes.length > 0 ? `${notes.length} message${notes.length !== 1 ? 's' : ''}` : 'Start a conversation'}
              </p>
            </div>
          </div>
          {onRequestFeature && (
            <button
              onClick={onRequestFeature}
              className="group px-3 sm:px-4 py-2 bg-gradient-to-br from-amber-400 to-amber-500 text-white rounded-xl hover:from-amber-500 hover:to-amber-600 transition-all text-sm font-medium flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <span className="text-base group-hover:scale-110 transition-transform">💡</span>
              <span className="hidden sm:inline">Request Feature</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className={messagesAreaClasses}>
        {notes.length === 0 ? (
          <div className="text-center py-16">
            <div className={`inline-flex items-center justify-center w-20 h-20 ${dark ? 'bg-zapt-purple/20' : 'bg-gradient-to-br from-zapt-purple/10 to-zapt-purple/5'} rounded-3xl mb-4`}>
              <svg className={`w-10 h-10 ${dark ? 'text-zapt-purple/60' : 'text-zapt-purple/40'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold ${dark ? 'text-white' : 'text-gray-700'} mb-2`}>No messages yet</h3>
            <p className={`${dark ? 'text-gray-400' : 'text-gray-500'} text-sm max-w-sm mx-auto`}>
              We'll post updates here as we build your app. Feel free to send us a message anytime!
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <span className={`px-3 py-1.5 ${dark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-full text-xs`}>📦 Build updates</span>
              <span className={`px-3 py-1.5 ${dark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-full text-xs`}>💬 Direct messages</span>
              <span className={`px-3 py-1.5 ${dark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-full text-xs`}>💡 Feature requests</span>
            </div>
          </div>
        ) : (
          <>
            {notes.map((note, index) => {
              const previousNote = index > 0 ? notes[index - 1] : null;
              const showDateSeparator = shouldShowDateSeparator(note, previousNote);
              const isCurrentUser = note.authorEmail === currentUserEmail;
              const isAdmin = isAdminEmail(note.authorEmail);
              
              return (
                <React.Fragment key={note.id}>
                  {showDateSeparator && <DateSeparator date={note.createdAt} />}
                  {note.noteType === 'system' ? (
                    <SystemEventItem note={note} />
                  ) : note.noteType === 'feature_request' ? (
                    <FeatureRequestItem note={note} isCurrentUser={isCurrentUser} />
                  ) : (
                    <MessageItem note={note} isCurrentUser={isCurrentUser} isAdmin={isAdminEmail(note.authorEmail)} />
                  )}
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className={inputAreaClasses}>
        <div className="flex gap-3 items-end">
          {/* Avatar for current user */}
          <div className="hidden sm:flex flex-shrink-0 w-10 h-10 bg-gradient-to-br from-zapt-purple to-zapt-purple/80 rounded-full items-center justify-center text-white text-sm font-semibold shadow-md">
            {currentUserEmail?.charAt(0).toUpperCase() || '?'}
          </div>
          
          {/* Input area */}
          <div className="flex-1 relative">
            <div className={inputWrapperClasses}>
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                rows={1}
                className={textareaClasses}
                style={{ maxHeight: '150px' }}
              />
              
              {/* Send button inside input */}
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || addingNote}
                className={`absolute right-2 bottom-2 p-2 ${dark ? 'bg-zapt-yellow text-zapt-navy hover:bg-zapt-yellow/90' : 'bg-gradient-to-br from-zapt-purple to-zapt-purple/90 text-white hover:from-zapt-purple/90 hover:to-zapt-purple/80'} rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none`}
              >
                {addingNote ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Helper text */}
            <div className="flex items-center justify-between mt-2 px-1">
              <p className={`text-xs ${helperTextClasses}`}>
                <span className="hidden sm:inline">Press </span>
                <kbd className={kbdClasses}>Enter</kbd>
                <span className="hidden sm:inline"> to send</span>
                <span className={`${dark ? 'text-gray-600' : 'text-gray-300'} mx-1.5`}>•</span>
                <kbd className={kbdClasses}>Shift+Enter</kbd>
                <span className="hidden sm:inline"> for new line</span>
              </p>
              {newMessage.length > 0 && (
                <p className={`text-xs ${newMessage.length > 2000 ? 'text-red-500' : helperTextClasses}`}>
                  {newMessage.length}/2000
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
